import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import P from '../../../../../../elements/Typo/P'
import Button from '../../../../../../elements/button'
import Text from '../../../../../../elements/Typo/Text'

import { useExperimentApi } from '../../../../../../store/api/hooks/experimentApiHook'
import { useNavigate, useParams } from 'react-router-dom'
import { URLS } from '../../../../../../_config'
import DataSelection from '../../../../../CreateExperiment/setScope/_components/uploadData/dataSelection'
import toast from 'react-hot-toast'
import Loader from '../../../../../../components/loader'
import Container from '../../../../../../elements/Container'
import DataFileUploader from '../../../../../../components/DataFileUploader'
import { SelectedDataTableType, UploadStatusType } from '../../../../../../_config/types'
import { trackEvent } from '../../../../../../_common/functions'

interface Props {
    next: () => void
}

const MTEUploadData: React.FC<Props> = ({ next }) => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const params = useParams()
    const experimentApi = useExperimentApi()

    const [working, setWorking] = useState<boolean>(false)

    const [selectedKPIMeasurementTable, setSelectedKPIMeasurementTable] = useState<SelectedDataTableType | null>(null)
    const [selectedInterventionMeasurementTable, setSelectedInterventionMeasurementTable] = useState<SelectedDataTableType | null>(null)
    const [kpiFileUploader, setKpiFileUploader] = useState<UploadStatusType>('ready-for-upload')
    const [interventionFileUploader, setInterventionFileUploader] = useState<UploadStatusType>('ready-for-upload')

    const handleKpiFileStatus = (status: UploadStatusType) => {
        if (status === 'uploading') {
            trackEvent('measurement_mte_kpi_file_upload_started', { experiment_literal: params.experiment })
        } else if (status === 'data-ready') {
            trackEvent('measurement_mte_kpi_file_upload_success', { experiment_literal: params.experiment })
        } else if (status === 'error') {
            trackEvent('measurement_mte_kpi_file_upload_failed', { experiment_literal: params.experiment, error_message: 'KPI file upload failed' })
        }
        setKpiFileUploader(status)
    }

    const handleInterventionFileStatus = (status: UploadStatusType) => {
        if (status === 'uploading') {
            trackEvent('measurement_mte_intervention_file_upload_started', { experiment_literal: params.experiment })
        } else if (status === 'data-ready') {
            trackEvent('measurement_mte_intervention_file_upload_success', { experiment_literal: params.experiment })
        } else if (status === 'error') {
            trackEvent('measurement_mte_intervention_file_upload_failed', { experiment_literal: params.experiment, error_message: 'Intervention file upload failed' })
        }
        setInterventionFileUploader(status)
    }

    const handleKpiTableSelected = (table: SelectedDataTableType | null) => {
        if (table) {
            trackEvent('measurement_mte_kpi_table_selected', { experiment_literal: params.experiment, table_name: table?.table?.name })
        }
        setSelectedKPIMeasurementTable(table)
    }

    const handleInterventionTableSelected = (table: SelectedDataTableType | null) => {
        if (table) {
            trackEvent('measurement_mte_intervention_table_selected', { experiment_literal: params.experiment, table_name: table?.table?.name })
        }
        setSelectedInterventionMeasurementTable(table)
    }

    const updateWithMeasurementTableData = () => {
        // delta_measurement_table_name: selectedKPIMeasurementTable.name,
        const payload: any = {
            literal: params.experiment,
        }
        if (selectedKPIMeasurementTable) {
            payload.delta_measurement_table_name = selectedKPIMeasurementTable?.table.name
        }
        if (selectedInterventionMeasurementTable) {
            payload.delta_intervention_table_name = selectedInterventionMeasurementTable?.table.name
        }
        setWorking(true)
        experimentApi.updateExperiment(
            payload,
            (data: any) => {
                setWorking(false)
                next()
            },
            (error: any) => {
                setWorking(false)
                toast.error(error?.message)
            },
        )
    }

    const isDataReady = () => {
        if ((selectedKPIMeasurementTable || kpiFileUploader === 'data-ready') && (selectedInterventionMeasurementTable || interventionFileUploader === 'data-ready')) {
            return !working
        }
        return false
    }

    return (
        <Container size="md">
            <p className="text-[1.125rem] font-bold mt-2">{t('Measurement Data Selection / Upload')}</p>

            <div className="grid grid-cols-2 gap-4 mt-3">
                <div>
                    <p className="text-[1rem] font-bold">{t('KPI Data:')}</p>
                    <Text size="sm" className="!text-[#676767] !text-[0.79rem]">
                        {t('Ensure your dataset contains Granularity, Date, and KPI columns to support accurate measurement and analysis.')}
                    </Text>
                    <P className="text-[#414040] font-semibold !mb-2 text-[0.677rem] mt-2">{t('Upload your own data')}</P>
                    <DataFileUploader uploadType="measurement" literal={params.experiment as string} uploadingStatus={handleKpiFileStatus} />

                    <div className="flex items-center justify-center my-4">
                        <div className="flex items-center justify-center leading-none text-[0.667rem] font-semibold w-[1.833rem] h-[1.833rem] bg-[#F4F4F4] border border-dashed border-[#BEBEBE] rounded-full">{t('OR')}</div>
                    </div>

                    <div className="flex-shrink-0 my-4">
                        <DataSelection extType="MTE" _for="measurement" selectedDataTable={handleKpiTableSelected} />
                    </div>
                </div>

                <div>
                    <p className="text-[1rem] font-bold">{t('Intervention Data:')}</p>
                    <Text size="sm" className="!text-[#676767] !text-[0.79rem]">
                        {t('Ensure your dataset contains Granularity and binary Touch Point columns (0 or 1) to support accurate intervention impact analysis.')}
                    </Text>
                    <P className="text-[#414040] font-semibold !mb-2 text-[0.677rem] mt-2">{t('Upload your own data')}</P>

                    <DataFileUploader uploadType="intervention" literal={params.experiment as string} uploadingStatus={handleInterventionFileStatus} />

                    <div className="flex items-center justify-center my-4">
                        <div className="flex items-center justify-center leading-none text-[0.667rem] font-semibold w-[1.833rem] h-[1.833rem] bg-[#F4F4F4] border border-dashed border-[#BEBEBE] rounded-full">{t('OR')}</div>
                    </div>
                    <div className="flex-shrink-0 my-4">
                        <DataSelection _for="intervention" selectedDataTable={handleInterventionTableSelected} />
                    </div>
                </div>
            </div>

            <div className="flex items-center justify-between my-4 ">
                <div className="flex items-center gap-3">
                    <Button
                        theme="secondary"
                        size="xs"
                        className="text-white"
                        onClick={() => {
                            void navigate(URLS.CREATE_EXPERIMENT(params.experiment as string))
                        }}
                    >
                        {t('Previous Step')}
                    </Button>
                </div>
                <div className="flex items-center gap-3">
                    <Button
                        theme="secondary"
                        size="xs"
                        className="text-white"
                        disabled={!isDataReady()}
                        onClick={() => {
                            updateWithMeasurementTableData()
                        }}
                    >
                        {working ? <Loader /> : t('NEXT STEP')}
                    </Button>
                </div>
            </div>

            <div className="flex gap-2 items-center">
                {/* <Icon name="envelop" security="0.7rem" />
                <Text size="xs"> 
                    <Trans i18nKey="Having trouble submitting your information? If your <bold>data</bold> is <bold>too large</bold>, please reach out to us directly via <bold>email</bold>" components={{ bold: <b /> }} />
                </Text> */}
            </div>
        </Container>
    )
}

export default MTEUploadData
